import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import type { ViewKey } from './Sidebar';

interface MobileNavProps {
  active: ViewKey;
  onChange: (view: ViewKey) => void;
  onOpenTelegramSettings: () => void;
  onLogout: () => void;
}

const navItems: { key: ViewKey; label: string; icon: string }[] = [
  { key: 'notes', label: 'Ghi chú', icon: 'ri-sticky-note-line' },
  { key: 'knowledge', label: 'Kiến thức', icon: 'ri-lightbulb-line' },
];

export default function MobileNav({
  active,
  onChange,
  onOpenTelegramSettings,
  onLogout,
}: MobileNavProps) {
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      {/* Mobile Bottom Bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 flex items-center justify-around border-t border-background-200 bg-background-50 px-2 pb-[env(safe-area-inset-bottom,0px)] pt-2 md:hidden">
        {navItems.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => onChange(item.key)}
            className={`flex flex-1 flex-col items-center gap-0.5 rounded-lg py-2 transition ${
              active === item.key
                ? 'text-primary-600'
                : 'text-foreground-400'
            }`}
          >
            <i className={`${item.icon} text-xl`} />
            <span className="text-[11px] font-medium">{item.label}</span>
          </button>
        ))}
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          className="flex flex-1 flex-col items-center gap-0.5 rounded-lg py-2 text-foreground-400 transition"
        >
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-secondary-500 text-[10px] font-bold text-background-50">
            {user?.email?.charAt(0).toUpperCase() ?? 'A'}
          </div>
          <span className="text-[11px] font-medium">Tài khoản</span>
        </button>
      </nav>

      {/* Mobile Menu Overlay */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm"
            onClick={() => setMenuOpen(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 rounded-t-2xl border-t border-background-200 bg-background-50 p-4 pb-[env(safe-area-inset-bottom,12px)] shadow-2xl">
            <div className="mb-4 flex items-center justify-center">
              <div className="h-1 w-10 rounded-full bg-background-300" />
            </div>

            <div className="mb-4 flex items-center gap-3 rounded-xl bg-background-100 px-4 py-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-500 text-sm font-semibold text-background-50">
                {user?.email?.charAt(0).toUpperCase() ?? 'A'}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground-900">
                  {user?.email ?? 'admin'}
                </p>
                <p className="text-xs text-foreground-400">Quản trị viên</p>
              </div>
            </div>

            <div className="space-y-1">
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  onOpenTelegramSettings();
                }}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-foreground-700 transition hover:bg-background-100"
              >
                <i className="ri-send-plane-2-line text-lg text-foreground-400" />
                Cảnh báo Telegram
              </button>
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  onLogout();
                }}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-accent-700 transition hover:bg-accent-50"
              >
                <i className="ri-logout-box-r-line text-lg text-accent-500" />
                Đăng xuất
              </button>
            </div>

            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              className="mt-3 w-full rounded-lg py-2.5 text-sm font-medium text-foreground-500 transition hover:bg-background-100"
            >
              Đóng
            </button>
          </div>
        </div>
      )}
    </>
  );
}